/**
 * Chart themes: the console-wide look of every chart, chosen in Settings →
 * Appearance. A theme owns the series colours, so a saved widget never carries
 * them and one switch repaints every dashboard without touching its data.
 *
 * Series are categorical, in a fixed order: a slot's neighbours are chosen for
 * contrast, including under colour-blind simulation, so never reorder or cycle
 * them by hand. Each palette must pass the dataviz validator in its own mode.
 */

/** Text sizes in pixels; one scale for the DOM card and the canvas it holds. */
export const CHART_TYPE = {
  title: 15,
  titleWeight: 600,
  value: 32,
  valueCompact: 24,
  valueWeight: 600,
  label: 12,
  axis: 11,
};

const CALM = {
  light: ['#3b72b9', '#cb5c7c', '#6d9a33', '#3f93bd', '#bd8210', '#16938a', '#c0633c', '#7863bb'],
  dark: ['#5c8fd6', '#d4648a', '#7a9e3c', '#449cc8', '#bb8418', '#249c8e', '#cf7046', '#8a74d4'],
};

/** A card's accent is a role: every theme gives all four, each mode its own. */
const CALM_TONES = {
  light: { teal: '#2f8a84', blue: '#4a6fa8', amber: '#b97f0b', rose: '#c8657a' },
  dark: { teal: '#3aa69f', blue: '#6e93cf', amber: '#e5a52a', rose: '#d97d90' },
};

/**
 * The interface's own language: indigo and a darkened lime lead the series. The
 * brand lime is a fill with dark text on it, and on white it never draws a thin
 * mark, so the accent is ink there and lime only as its pill and callout text.
 * On a dark surface lime carries the marks itself.
 */
const EVOLVE = {
  light: ['#3347c8', '#7e9c00', '#2c8fc4', '#e2642c', '#7a5cf0', '#bc8400', '#0e9a72', '#b5479a'],
  dark: ['#6a80f0', '#869e12', '#3f97c6', '#de6e3c', '#9175ea', '#bd8914', '#22a481', '#c25aa7'],
};

export const CHART_THEMES = ['calm', 'evolve'];
export const DEFAULT_CHART_THEME = 'calm';

/** A stored value could be anything; only a known theme reaches the tokens. */
export const chartThemeId = (value) => {
  if (value === 'console') return 'evolve';
  return CHART_THEMES.includes(value) ? value : DEFAULT_CHART_THEME;
};

const channels = (hex) => [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16));

/** `amount` of `colour` over `base`; both are opaque #rrggbb. */
export const mix = (colour, base, amount) => {
  const under = channels(base);
  const parts = channels(colour).map((value, index) => Math.round(value * amount + under[index] * (1 - amount)));
  return `#${parts.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
};

const luminance = (hex) => {
  const [red, green, blue] = channels(hex).map((value) => {
    const part = value / 255;
    return part <= 0.04045 ? part / 12.92 : ((part + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

const contrast = (one, other) => {
  const [light, dark] = [luminance(one), luminance(other)].sort((a, b) => b - a);
  return (light + 0.05) / (dark + 0.05);
};

/** Everything a card draws in one accent, derived so a theme lists it once. */
export const tone = (colour, { surface, ink }) => ({
  colour,
  on: contrast('#FFFFFF', colour) >= contrast('#17171A', colour) ? '#FFFFFF' : '#17171A',
  soft: mix(colour, surface, 0.22),
  pill: `${colour}24`,
  pillText: mix(colour, ink, 0.55),
});

/** The accent roles in Evolve's own tokens; `teal` is the default, the brand one. */
const evolveTones = (shade, colours) => ({
  teal: shade === 'dark'
    ? { ...tone(colours.accent, colours), pill: `${colours.accent}24`, pillText: colours.accent }
    : { ...tone(colours.ink, colours), on: colours.accent, pill: colours.accent, pillText: colours.onAccent },
  blue: tone(colours.link, colours),
  amber: tone(colours.warningFill, colours),
  rose: tone(colours.negativeFill, colours),
});

/** One theme in one mode. */
export const chartTheme = (id, mode, colours) => {
  const known = chartThemeId(id);
  const shade = mode === 'dark' ? 'dark' : 'light';
  const evolve = known === 'evolve';
  const tones = evolve ? evolveTones(shade, colours)
    : Object.fromEntries(Object.entries(CALM_TONES[shade]).map(([name, colour]) => [name, tone(colour, colours)]));
  return {
    id: known,
    series: (evolve ? EVOLVE : CALM)[shade],
    tones,
    ...(evolve ? { defaultTone: 'teal' } : {}),
    neutral: { bar: colours.borderField, average: colours.iconMuted, tag: colours.ink, tagOn: colours.surface },
    type: CHART_TYPE,
  };
};
